import { useEffect, useMemo, useState } from 'react';
import { useAppState } from '../../context/LoadTestContext';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  Scatter,
  ScatterChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Box,
  Button,
  Skeleton,
  Typography,
} from '@mui/material';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { useTheme } from '@mui/material/styles';
import { fitAxis } from './axisScale';
import { useUnits } from '../units';
import { isUnloadStep, stepLabel } from '../steps';


function selectDisplaySteps(allSteps, maxSteps = 6) {
  const sorted = [...allSteps].sort((a, b) => a.step - b.step);

  if (sorted.length <= maxSteps) return sorted;

  return Array.from({ length: maxSteps }, (_, i) => {
    const index = Math.round(
      (i * (sorted.length - 1)) / (maxSteps - 1)
    );
    return sorted[index];
  });
}


function StrainGaugeGraph() {
  const { loading, allStrainGraphData, showUnloadSteps, strainGauges, sgConfirmed, selectedBox, measurementUnits } = useAppState();

  const rawStrain = Object.values(measurementUnits?.sg ?? {}).some((unit) => unit === 'digits');

  const strainUnitLabel = rawStrain ? 'digits' : 'με';

  const theme = useTheme();

  // Clicking a step's profile zooms to that step alone; null shows them all.
  const [zoomStep, setZoomStep] = useState(null);
  useEffect(() => setZoomStep(null), [selectedBox?.id]);

  // The load-step profiles are the main picture: up to six, sampled evenly and
  // always including the last one, which is drawn in blue. With unload steps
  // shown, up to four unload profiles are drawn over them, dashed and in the
  // Unload tag's colour, so they don't crowd the load steps out of the sample.
  const everyStep = Array.isArray(allStrainGraphData) ? allStrainGraphData : [];
  const loadSteps = everyStep.filter((s) => !isUnloadStep(s));
  const unloadSteps = showUnloadSteps ? everyStep.filter(isUnloadStep) : [];
  const allSteps = [...loadSteps, ...unloadSteps];
  // Looked up in every step, not just the sample, so a refresh that changes
  // the sample doesn't drop the zoom; a step that's gone falls back to all.
  const zoomed = zoomStep != null && allSteps.some((s) => s.step === zoomStep);

  // Once a calibration revision has been saved for the test, skip the dot for
  // any channel whose gauge has no calibration factor or was excluded.
  const skippedChannels = useMemo(() => new Set(
    sgConfirmed
      ? strainGauges
        // .filter((g) => g.excluded || g.CalibrationFactor === '' || g.CalibrationFactor == null)
        .filter((g) => g.excluded)
        .map((g) => g.channel)
      : []
  ), [strainGauges, sgConfirmed]);

  const displayedStrainGraphData = useMemo(() => {
    if (rawStrain) return [];
    return (zoomed
      ? allSteps.filter((s) => s.step === zoomStep)
      : [...selectDisplaySteps(loadSteps), ...selectDisplaySteps(unloadSteps, 4)]
    ).map((entry) => ({
      step: entry.step,
      label: stepLabel(entry),
      unload: isUnloadStep(entry),
      data: entry.data.filter((point) => !skippedChannels.has(point.strain_gauge)),
    }));
  }, [allStrainGraphData, showUnloadSteps, skippedChannels, zoomed, zoomStep]
  );

  // The last load step shown (or the one zoomed to) is the highlighted one.
  const latest_step = [...displayedStrainGraphData].reverse().find((s) => !s.unload)?.step;

  const units = useUnits();

  // Plot each gauge at its real depth once a revision gives the depths; until then
  // only the logger channel is known, so fall back to that and say so. Depths
  // are stored in metres and shown in the selected unit.
  const depthByChannel = useMemo(() => {
    if (!sgConfirmed) return null;
    const map = new Map();
    strainGauges.forEach((g) => {
      if (g.channel != null && Number.isFinite(g.DepthM)) map.set(g.channel, units.toDepth(g.DepthM));
    });
    return map.size ? map : null;
  }, [strainGauges, sgConfirmed, units.system]);
  // const byDepth = Boolean(depthByChannel);
  const plottedChannels = new Set(
    displayedStrainGraphData.flatMap(({ data }) => data.filter((point) => Number.isFinite(point.microstrain)).map((point) => point.strain_gauge))
  );

  const byDepth = plottedChannels.size > 0 && depthByChannel !== null && [...plottedChannels].every((channel) => depthByChannel.has(channel));

  const serialByChannel = useMemo(
    () => new Map(strainGauges.filter((g) => g.channel != null).map((g) => [g.channel, g.SerialNumber])),
    [strainGauges]
  );

  // Each point gets a `y`: depth (display unit) or channel. Lines connect points in
  // array order, so sort top to bottom; otherwise gauges wired out of depth
  // order would zigzag.
  const plotted = useMemo(() => displayedStrainGraphData.map(({ step, label, unload, data }) => ({
    step,
    label,
    unload,
    data: data
      .map((p) => ({
        ...p,
        label,
        y: byDepth ? depthByChannel.get(p.strain_gauge) : p.strain_gauge,
        serial: serialByChannel.get(p.strain_gauge),
      }))
      .filter((p) => p.y !== undefined && Number.isFinite(p.microstrain))
      .sort((a, b) => a.y - b.y),
  })), [displayedStrainGraphData, byDepth, depthByChannel, serialByChannel]);

  const allPoints = plotted.flatMap((s) => s.data);

  // Microstrain from zero out to the largest reading, in round steps. Zoomed to
  // one step, fit just that step's readings so its shape fills the plot.
  const strainAxis = zoomed
    ? fitAxis(allPoints.map((p) => p.microstrain), { minSpan: 10 })
    : fitAxis([0, ...allPoints.map((p) => p.microstrain)]);

  // Depth from the top (0) down past the deepest gauge. In channel mode, one
  // tick per channel.
  const gaugeYs = [...new Set(allPoints.map((p) => p.y))].sort((a, b) => a - b);
  const depthAxis = byDepth ? fitAxis([0, ...gaugeYs], { targetTicks: 8 }) : null;
  const yDomain = byDepth
    ? depthAxis.domain
    : [Math.max(0, (gaugeYs[0] ?? 1) - 0.5), (gaugeYs.at(-1) ?? 1) + 0.5];

  const gaugeLabel = (y) => {
    const p = allPoints.find((pt) => pt.y === y);
    return p ? `SG${p.strain_gauge}` : '';
  };

  const CustomLegend = ({ payload }) => {
    const greyItems = payload.filter(
      p => p.color === theme.chart.tooltipText
    );

    const blueItems = payload.filter(
      p => p.color === theme.palette.primary.main
    );

    const Item = ({ color, label }) => (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginLeft: 10,
        }}
      >
        <div
          style={{
            width: 12,
            height: 2,
            backgroundColor: color,
            marginRight: 6,
          }}
        />
        <span>{label}</span>
      </div>
    );


    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: 'flex-end', width: '100%', paddingRight: 24 }}>
        {greyItems.length > 0 && (
          <Item
            color={theme.chart.tooltipText}
            label="steps 1–5"
          />
        )}

        {blueItems.map(item => (
          <Item
            key={item.value}
            color={item.color}
            label={item.value}
          />
        ))}
      </div>
    );
  };

  const StrainTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;

    const point = payload[0].payload;
    return (
      <div
        style={{
          background: theme.chart.tooltipBg,
          color: theme.palette.text.secondary,
          padding: '8px 10px',
          borderRadius: 4,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Step {point.label}</div>
        <div>Gauge: SG{point.strain_gauge}{point.serial ? ` (${point.serial})` : ''}</div>
        {byDepth && <div>Depth: {Number(point.y).toFixed(2)} {units.depthUnit}</div>}
        <div>{rawStrain ? 'Reading' : 'Strain'}: {Number(point.microstrain).toFixed(1)} {strainUnitLabel}</div>
      </div>
    );
  };

  const emptyMessage = rawStrain ? 'Calibrate at least one strain gauge to plot Strain' : !allPoints.length ? 'No strain profiles available for the selected steps.' : null;

  return (
    <Box
      sx={{ height: '100%', minHeight: 165, position: 'relative', '& .recharts-scatter-symbol': { cursor: zoomed ? 'default' : 'pointer' } }}
      onDoubleClick={() => setZoomStep(null)}
    >
      {!loading && emptyMessage && (
        <Box
          role='status'
          sx={{
            position: 'absolute',
            inset:0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 2,
            p: 3,
          }}
        >
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              px: 2,
              py: 1,
              textAlign: 'center',
            }}
          >
            {emptyMessage}
          </Typography>
        </Box>

      )}
      {!loading && plotted.length > 0 && (
        zoomed ? (
          <Button
            size='small'
            variant='contained'
            onClick={() => setZoomStep(null)}
            startIcon={<ZoomOutMapIcon sx={{ fontSize: 16 }} />}
            sx={{ position: 'absolute', right: 16, bottom: byDepth ? 10 : 28, zIndex: 2, py: 0.25, px: 1, minWidth: 0, fontSize: 12, textTransform: 'none' }}
          >
            Reset zoom
          </Button>
        ) : null
      )}
      {!loading && !emptyMessage && !byDepth && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ position: 'absolute', right: 16, bottom: 10, zIndex: 1, fontSize: 11, pointerEvents: 'none' }}
        >
          Add gauge depths under Calibration to plot by depth
        </Typography>
      )}
      {loading ?
        <Skeleton variant='rectangular' animation='wave' sx={{ borderRadius: 1, width: { xl: 1195, lg: 605 }, height: 165 }} />
        :
        <ResponsiveContainer width='100%' height='100%' minHeight={165}>
          {/* <LineChart data={data} margin={{ top: 5, right: 10, left: 5, bottom: 10 }}>
            <CartesianGrid strokeDasharray='3 3' syncWithTicks={true} />

            <XAxis dataKey='load' type='number' domain={[0, 2000]} tickCount={11} fontSize={12} orientation='top'>
              <Label value='Load (kN)' fontSize={12} dy={-15} />
            </XAxis>

            <YAxis type='number' domain={[0, 8]} tickCount={9} fontSize={12} orientation='left' reversed>
              <Label value='Elevation (m)' fontSize={12} angle={-90} verticalAnchor='middle' dx={-10} />
            </YAxis>


            <Tooltip contentStyle={{ backgroundColor: theme.chart.tooltipBg, color: theme.palette.text.secondary }} />

            <Legend align='right' verticalAlign='top' content={<CustomLegend />} wrapperStyle={{ fontSize: 12, top: 0, right: 0 }} />


            <Line dataKey='step1' stroke={theme.chart.tooltipText} fill={theme.chart.tooltipText} strokeWidth={1.5} dot={{ strokeWidth: 1 }} />
            <Line dataKey='step2' stroke={theme.chart.tooltipText} fill={theme.chart.tooltipText} strokeWidth={1.5} dot={{ strokeWidth: 1 }} />
            <Line dataKey='step3' stroke={theme.chart.tooltipText} fill={theme.chart.tooltipText} strokeWidth={1.5} dot={{ strokeWidth: 1 }} />
            <Line dataKey='step4' stroke={theme.chart.tooltipText} fill={theme.chart.tooltipText} strokeWidth={1.5} dot={{ strokeWidth: 1 }} />
            <Line dataKey='step5' stroke={theme.chart.tooltipText} fill={theme.chart.tooltipText} strokeWidth={1.5} dot={{ strokeWidth: 1 }} />
            <Line dataKey='step6' stroke={theme.palette.primary.main} fill={theme.palette.primary.main} strokeWidth={1.5} dot={{ strokeWidth: 1 }} />
          </LineChart> */}
          <ScatterChart margin={{ top: 22, right: 12, left: 6, bottom: 6 }}>
            <CartesianGrid strokeDasharray='3 3' />

            <XAxis
              dataKey='microstrain'
              type='number'
              orientation='top'
              domain={strainAxis?.domain ?? [0, 'auto']}
              ticks={strainAxis?.ticks}
              tickFormatter={strainAxis?.format}
              interval={0}
              fontSize={10}
            >
              <Label value={rawStrain ? 'Gauge Readings (digits)' : 'Strain (µε)'} fontSize={12} position='top' offset={10} />
            </XAxis>

            <YAxis
              yAxisId='depth'
              dataKey='y'
              type='number'
              reversed
              domain={yDomain}
              ticks={byDepth ? depthAxis.ticks : gaugeYs}
              tickFormatter={byDepth ? depthAxis.format : (v) => `SG${v}`}
              interval={0}
              allowDecimals={byDepth}
              fontSize={10}
            >
              <Label
                value={byDepth ? `Depth (${units.depthUnit})` : 'Gauge (channel)'}
                fontSize={12}
                angle={-90}
                position='insideLeft'
                style={{ textAnchor: 'middle' }}
                dx={4}
              />
            </YAxis>

            {/* Gauge names at their depths, on the right. */}
            {byDepth && (
              <YAxis
                yAxisId='gauges'
                orientation='right'
                type='number'
                reversed
                domain={yDomain}
                ticks={gaugeYs}
                tickFormatter={gaugeLabel}
                interval={0}
                width={40}
                axisLine={false}
                tickLine={false}
                fontSize={10}
                tick={{ fill: theme.palette.primary.main }}
              />
            )}

            <Tooltip content={<StrainTooltip />} cursor={{ strokeDasharray: '3 3' }} />

            {plotted.map(({ step, label, unload, data }) => {
              const isLatest = step == latest_step;
              const color = unload ? theme.palette.warning.main : (isLatest ? '#0ea5e9' : theme.chart.tooltipText);

              return (
                <Scatter
                  key={step}
                  yAxisId='depth'
                  name={`Step ${label}`}
                  data={data}
                  fill={color}
                  line={{ stroke: color, strokeWidth: isLatest ? 2 : 1, strokeDasharray: unload ? '5 4' : undefined }}
                  isAnimationActive={false}
                  onClick={() => setZoomStep(step)}
                />
              )
            })}
          </ScatterChart>
        </ResponsiveContainer>
      }
    </Box>
  );
}

export default StrainGaugeGraph;
